import axios from "axios";
import {
  EMPTY_STRING,
  LOGS_TYPES,
  PAYRILLIUM_CONSTANTS,
  storeConstants,
  toastType,
} from "../constants";
import { getItemFromLocalStorage } from "../system/storage";
import { logErrorsService } from "./logErrors.service";
import { customToast } from "../shared/utility";

/**
 * Returns the current Payrillium base URL from localStorage.
 * Called at request time so it always reflects the latest stored value.
 */
const getPayrilliumBaseUrl = () =>
  getItemFromLocalStorage(storeConstants.PAYRILLIUM_BASE_URL_STORAGE_KEY);

const handlePayrilliumError = (error) => {
  console.error("Payrillium API error:", error);
  throw error;
};

const payrilliumPostRequest = (endpoint, data, headers = {}) => {
  if (!getPayrilliumBaseUrl()) {
    return Promise.reject(
      new Error(storeConstants.PAYRILLIUM_DEVICE_NOT_FOUND_ERROR)
    );
  }
  return axios
    .post(`${getPayrilliumBaseUrl()}/payrillium/${endpoint}`, data, { headers })
    .then((response) => response?.data, handlePayrilliumError);
};

const payrilliumGetRequest = (endpoint, config = {}) => {
  if (!getPayrilliumBaseUrl()) {
    return Promise.reject(
      new Error(storeConstants.PAYRILLIUM_DEVICE_NOT_FOUND_ERROR)
    );
  }

  return axios
    .get(`${getPayrilliumBaseUrl()}/payrillium/${endpoint}`, config)
    .then((response) => response?.data, handlePayrilliumError);
};

const payrilliumCapturePayment = ({ href, ...data }) => {
  return axios.post(
    `https://${process.env.REACT_APP_PAYRILLIUM_URL}/${href}`,
    {
      ...data,
    },
    {
      "Access-Control-Allow-Origin": true,
    }
  );
};

const logPayrilliumAction = (store, data = {}) => {
  if (!store?.id || !store?.storeName) return;
  logErrorsService.logErrors({
    data,
    type: LOGS_TYPES.PAYRILLIUM_LOGS,
  });
};

const payrilliumAbortTransactionOnFailure = async (error) => {
  await payrilliumAbortTransaction().catch(() => {});
  throw error;
};

const getDesktopConfig = (terminal = "", terminalIp = "") =>
  payrilliumGetRequest("local/desktop_config", {
    params: { terminal, terminalIp },
  }).catch(payrilliumAbortTransactionOnFailure);

const getHostConfig = () =>
  payrilliumGetRequest("local/host_config").catch(
    payrilliumAbortTransactionOnFailure
  );

const setHostConfig = (config = {}) =>
  payrilliumPostRequest("local/host_config", config).catch(
    payrilliumAbortTransactionOnFailure
  );

const getMerchantConfig = () =>
  payrilliumGetRequest("local/merchant_config").catch(
    payrilliumAbortTransactionOnFailure
  );

const setMerchantConfig = (config = {}) => {
  const formData = new FormData();
  Object.entries(config).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, value);
    }
  });
  return payrilliumPostRequest("local/merchant_config", formData, {
    "Content-Type": "multipart/form-data",
  }).catch(payrilliumAbortTransactionOnFailure);
};

// --------------- Terminal UI ---------------

const showBasket = (items = [], totalAmount = 0) =>
  payrilliumPostRequest("local/basket", {
    data: {
      items: items.map((item) => ({
        description: item.itemName || item.name || "Item",
        unitPrice: Number(item.actualPrice || item.price || 0),
        quantity: Number(item.quantity || 1),
        unitOfMeasure: "EA",
      })),
      totalAmount: Number(totalAmount).toFixed(2),
      currencyCode: "USD",
    },
  }).catch(payrilliumAbortTransactionOnFailure);

const getCardType = () =>
  payrilliumGetRequest("local/card_type").catch(
    payrilliumAbortTransactionOnFailure
  );

const testPayrilliumConnection = () => payrilliumGetRequest("local/test");

// --------------- Payment ---------------

const authorizePayment = ({
  payment_id,
  amount,
  cardType = PAYRILLIUM_CONSTANTS.CARD_TYPE.CREDIT,
  tip = PAYRILLIUM_CONSTANTS.TIP.DEFAULT,
  cash_back = PAYRILLIUM_CONSTANTS.CASH_BACK.DEFAULT,
  enableCtlessReading = true,
  enableContactReading = true,
  enableSwipeReading = true,
  enableManualReading = true,
  paymentTimeOut = PAYRILLIUM_CONSTANTS.TIMEOUT,
  signature,
}) =>
  payrilliumPostRequest("payment/auth", {
    cardType,
    payment_id,
    amount: Number(amount),
    tip,
    cash_back,
    signature,
    enableCtlessReading,
    enableContactReading,
    enableSwipeReading,
    enableManualReading,
    paymentTimeOut,
  }).catch(payrilliumAbortTransactionOnFailure);

const reverseAuthorization = ({
  payment_id,
  transaction_id,
  totalAmount,
  reason = "CUSTOMER_CANCELLATION",
}) =>
  payrilliumPostRequest("payment/auth_reversal", {
    payment_id,
    transaction_id,
    totalAmount,
    reason,
  }).catch(payrilliumAbortTransactionOnFailure);

const refundPayment = ({ payment_id, transaction_id, amount }) =>
  payrilliumPostRequest("payment/refund", {
    payment_id,
    transaction_id,
    amount: amount.toString(),
  }).catch(payrilliumAbortTransactionOnFailure);

const payrilliumAbortTransaction = () => payrilliumPostRequest("payment/abort");

// --------------- Config Initialization ---------------

/**
 * Validates and pushes merchant/host config to the terminal if not already done
 * this session. Reads credentials from the store object.
 */
const initializeTerminalConfig = async (paymentDevice = {}) => {
  const {
    merchantId = EMPTY_STRING,
    merchantKeyId = EMPTY_STRING,
    merchantsecretKey = EMPTY_STRING,
    mccCode = EMPTY_STRING,
  } = paymentDevice;
  try {
    const merchantConfig = await getMerchantConfig();
    if (!merchantConfig?.success || !merchantConfig?.data) {
      await setMerchantConfig({
        merchantId: merchantId,
        apiKeyId: merchantKeyId,
        secretKey: merchantsecretKey,
        mcc: mccCode,
        runEnvironment: process.env.REACT_APP_PAYRILLIUM_URL,
      });
    }

    const hostConfig = await getHostConfig();
    if (!hostConfig?.success || !hostConfig?.data) {
      const terminalId = paymentDevice?.poiId || EMPTY_STRING;
      await setHostConfig({
        industryDataType: "NONE",
        demoMode: paymentDevice.isDemoMode,
        showRemoveCard: true,
        terminalId,
      });
    }
  } catch (error) {
    console.error("Error initializing terminal config:", error);
    customToast(error?.toString(), toastType.ERROR);
  }
};

export {
  payrilliumPostRequest,
  payrilliumGetRequest,
  getPayrilliumBaseUrl,
  payrilliumCapturePayment,
  payrilliumAbortTransaction,
  getDesktopConfig,
  getHostConfig,
  setHostConfig,
  getMerchantConfig,
  setMerchantConfig,
  showBasket,
  getCardType,
  testPayrilliumConnection,
  authorizePayment,
  reverseAuthorization,
  refundPayment,
  initializeTerminalConfig,
  logPayrilliumAction,
};
